import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subMonths,
  startOfYear,
  endOfYear,
} from 'date-fns';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';

export type TimeframeValue = 'week' | 'month' | '3months' | 'year';

interface TimeframeSelectorProps {
  value: TimeframeValue;
  onChange: (value: TimeframeValue, start: Date, end: Date) => void;
  className?: string;
}

const timeframes: { value: TimeframeValue; labelKey: string }[] = [
  { value: 'week', labelKey: 'dashboard.timeframe.week' },
  { value: 'month', labelKey: 'dashboard.timeframe.month' },
  { value: '3months', labelKey: 'dashboard.timeframe.3months' },
  { value: 'year', labelKey: 'dashboard.timeframe.year' },
];

const getDateRange = (timeframe: TimeframeValue): { start: Date; end: Date } => {
  const now = new Date();

  switch (timeframe) {
    case 'week':
      return {
        start: startOfWeek(now, { weekStartsOn: 1 }),
        end: endOfWeek(now, { weekStartsOn: 1 }),
      };
    case 'month':
      return {
        start: startOfMonth(now),
        end: endOfMonth(now),
      };
    case '3months':
      return {
        start: startOfMonth(subMonths(now, 2)),
        end: endOfMonth(now),
      };
    case 'year':
      return {
        start: startOfYear(now),
        end: endOfYear(now),
      };
    default:
      return {
        start: startOfMonth(now),
        end: endOfMonth(now),
      };
  }
};

export const TimeframeSelector: React.FC<TimeframeSelectorProps> = ({
  value,
  onChange,
  className,
}) => {
  const { t } = useTranslation();

  // Trigger initial load on mount
  useEffect(() => {
    const range = getDateRange(value);
    onChange(value, range.start, range.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (newValue: TimeframeValue) => {
    const range = getDateRange(newValue);
    onChange(newValue, range.start, range.end);
  };

  return (
    <div className={cn('grid grid-cols-2 gap-1 p-1 bg-muted rounded-lg sm:flex', className)}>
      {timeframes.map((tf) => (
        <Button
          key={tf.value}
          variant={value === tf.value ? 'default' : 'ghost'}
          size="sm"
          onClick={() => handleClick(tf.value)}
          className={cn(
            'w-full transition-all sm:w-auto',
            value === tf.value
              ? 'bg-background shadow-sm text-foreground hover:bg-background'
              : 'hover:bg-background/50'
          )}
        >
          {t(tf.labelKey, tf.value === 'week' ? 'This Week' :
            tf.value === 'month' ? 'This Month' :
            tf.value === '3months' ? 'Last 3 Months' : 'This Year')}
        </Button>
      ))}
    </div>
  );
};

export default TimeframeSelector;
